import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { AlarmClock, Check, Clock, Info, SkipForward } from 'lucide-react-native';
import { ComponentType, useRef } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { getSubtypeMeta, getSystemMeta } from '@/data/systems';
import { dueLabel, type ActiveTask } from '@/lib/tasks';
import { resolveIcon } from '@/lib/rule-icon';

interface Props {
  task: ActiveTask;
  onComplete: (point: { x: number; y: number }) => void;
  /** Optional — when wired, renders a Skip button between Mark Done and the reschedule icon. */
  onSkip?: () => void;
  onSnooze: () => void;
  /** Long-press shortcut on the snooze button: jump straight to tomorrow. */
  onSnoozeTomorrow?: () => void;
  disabled?: boolean;
}

export function HeroCard({ task, onComplete, onSkip, onSnooze, onSnoozeTomorrow, disabled }: Props) {
  const router = useRouter();
  const system = getSystemMeta(task.system_kind);
  const subtype = getSubtypeMeta(task.system_kind, task.system_subtype);
  const Icon: ComponentType<{
    size?: number;
    color?: string;
    strokeWidth?: number;
  }> = resolveIcon(task.icon ?? system?.icon ?? null);
  const diff = task.difficulty ?? 'easy';
  const diffLabel = diff === 'easy' ? 'Easy' : diff === 'moderate' ? 'Moderate' : 'Hard';
  const systemLabel = task.system_nickname
    ? task.system_nickname.toUpperCase()
    : subtype
    ? `${(system?.label ?? task.system_kind ?? 'HOME').toUpperCase()} · ${subtype.label.toUpperCase()}`
    : (system?.label ?? 'Home').toUpperCase();

  const btnRef = useRef<View>(null);

  const handleDone = () => {
    btnRef.current?.measureInWindow((px, py, w, h) => {
      onComplete({ x: px + w / 2, y: py + h / 2 });
    });
  };

  return (
    <LinearGradient
      colors={[Palette.forestLight, Palette.forest]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={styles.hero}>
      <View style={styles.eyebrow}>
        <View style={styles.dot} />
        <Text style={styles.eyebrowText}>
          {dueLabel(task.due_at).toUpperCase()} · {systemLabel}
        </Text>
      </View>

      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Open task details"
        onPress={() => router.push(`/task/${task.id}` as never)}
        style={styles.heroIcon}>
        <Icon size={22} color={Palette.cream} strokeWidth={1.7} />
      </Pressable>

      <Pressable
        onPress={() => router.push(`/task/${task.id}` as never)}
        style={styles.titleTap}
        accessibilityLabel="Open task details">
        <Text style={styles.title}>{task.title}</Text>
        {/* 4-line cap — desktop column is wider so we can show more of
            the description without dominating the card; mobile gets the
            same cap which is usually 2-3 visual lines given the narrower
            width. Tap into task detail for the full why. */}
        {task.why ? <Text style={styles.why} numberOfLines={4}>{task.why}</Text> : null}
        <View style={styles.learnMore}>
          <Info size={11} color={Palette.honeySoft} strokeWidth={2} />
          <Text style={styles.learnMoreText}>How &amp; why · tap to learn</Text>
        </View>
      </Pressable>

      <View style={styles.metaRow}>
        <View style={styles.pill}>
          <Clock size={12} color={Palette.cream} strokeWidth={1.6} />
          <Text style={styles.pillText}>{task.time_minutes ?? 10} min</Text>
        </View>
        <View style={[styles.pill, diff === 'easy' ? styles.pillEasy : styles.pillModerate]}>
          <Text
            style={[
              styles.pillText,
              diff === 'easy' ? styles.pillEasyText : styles.pillModerateText,
            ]}>
            {diffLabel}
          </Text>
        </View>
      </View>

      <View style={styles.actions}>
        <Pressable
          ref={btnRef}
          accessibilityRole="button"
          accessibilityLabel="Mark done"
          disabled={disabled}
          onPress={handleDone}
          style={({ pressed }) => [
            styles.btnDone,
            pressed && styles.btnPressed,
            disabled && { opacity: 0.7 },
          ]}>
          <Check size={18} color="#3A2A0C" strokeWidth={2.4} />
          <Text style={styles.btnDoneText}>Mark Done</Text>
        </Pressable>
        {onSkip ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Skip this one"
            disabled={disabled}
            onPress={onSkip}
            style={({ pressed }) => [
              styles.btnSkip,
              pressed && styles.btnPressed,
            ]}>
            <SkipForward size={18} color={Palette.cream} strokeWidth={1.8} />
          </Pressable>
        ) : null}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Reschedule — long-press for tomorrow"
          disabled={disabled}
          onPress={onSnooze}
          onLongPress={onSnoozeTomorrow}
          delayLongPress={350}
          style={({ pressed }) => [
            styles.btnSnooze,
            pressed && styles.btnPressed,
          ]}>
          <AlarmClock size={19} color={Palette.cream} strokeWidth={1.7} />
        </Pressable>
      </View>
    </LinearGradient>
  );
}

export function HeroEmpty({ next }: { next?: string }) {
  return (
    <LinearGradient
      colors={[Palette.forestLight, Palette.forest]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={[styles.hero, styles.emptyHero]}>
      <View style={styles.emptyMark}>
        <Check size={28} color={Palette.honey} strokeWidth={2.6} />
      </View>
      <Text style={[styles.title, styles.emptyTitle]}>You&apos;re all caught up</Text>
      <Text style={[styles.why, styles.emptyWhy]}>
        Nothing else needs you this week.{next ? ` Next up: ${next}.` : ''}
      </Text>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  hero: {
    borderRadius: Radius['3xl'],
    padding: Spacing.five,
    overflow: 'hidden',
  },
  eyebrow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: Palette.honey,
  },
  eyebrowText: {
    fontSize: 12,
    fontFamily: Fonts.sansBold,
    letterSpacing: 1.1,
    color: Palette.honeySoft,
    flex: 1,
  },
  heroIcon: {
    position: 'absolute',
    top: 20,
    right: 20,
    width: 46,
    height: 46,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleTap: {
    paddingRight: 54,
  },
  title: {
    fontFamily: Fonts.serifBold,
    fontSize: 24,
    lineHeight: 30,
    color: Palette.cream,
    letterSpacing: -0.4,
    marginTop: 13,
  },
  why: {
    marginTop: 9,
    fontSize: 13.5,
    lineHeight: 20,
    color: 'rgba(251,246,234,0.78)',
    fontFamily: Fonts.sans,
    // No maxWidth, no padding — the parent `titleTap` already has
    // `paddingRight: 54` to clear the icon. The earlier paddingRight here
    // was additive on top of that, robbing ~54px of text width and
    // wrapping the description tight even on the wider desktop column.
  },
  learnMore: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  learnMoreText: {
    fontSize: 12,
    fontFamily: Fonts.sansBold,
    color: Palette.honeySoft,
    letterSpacing: 0.4,
  },
  metaRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 15,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 11,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.07)',
  },
  pillText: {
    fontSize: 13,
    fontFamily: Fonts.sansSemibold,
    color: Palette.cream,
  },
  pillEasy: { backgroundColor: 'rgba(123,168,120,0.28)' },
  pillEasyText: { color: '#D4E8CC' },
  pillModerate: { backgroundColor: 'rgba(196,123,75,0.3)' },
  pillModerateText: { color: '#F0CDA8' },
  actions: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 18,
  },
  btnDone: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    backgroundColor: Palette.honey,
    paddingVertical: 15,
    paddingHorizontal: 18,
    borderRadius: 18,
  },
  btnDoneText: {
    color: '#3A2A0C',
    fontFamily: Fonts.sansBold,
    fontSize: 15.5,
    letterSpacing: 0.1,
  },
  btnSnooze: {
    width: 52,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.09)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnSkip: {
    // Clay-tinted so it reads as "I'm intentionally moving past this"
    // — different mood than the honey-bright Mark Done. Same height as
    // the reschedule icon-only button so the row stays balanced.
    width: 52,
    borderRadius: 18,
    backgroundColor: 'rgba(176,95,60,0.5)',
    borderWidth: 1,
    borderColor: 'rgba(176,95,60,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnPressed: {
    transform: [{ scale: 0.965 }],
  },
  emptyHero: {
    alignItems: 'center',
  },
  emptyMark: {
    width: 62,
    height: 62,
    borderRadius: 31,
    borderWidth: 1.5,
    borderColor: 'rgba(225,163,61,0.5)',
    backgroundColor: 'rgba(225,163,61,0.18)',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 6,
  },
  emptyTitle: {
    textAlign: 'center',
    marginTop: 14,
  },
  emptyWhy: {
    textAlign: 'center',
    marginTop: 8,
    maxWidth: 280,
    alignSelf: 'center',
  },
});
